import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, ReferenceLine, AreaChart, Area, BarChart, Bar
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, Activity, Target, Heart,
  Calendar, RefreshCw, Loader2, CheckCircle2, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrendIndicator = ({ value, benchmark, label }) => {
  const diff = value - benchmark;
  const isGood = diff >= 0;
  
  return (
    <div className="flex items-center gap-2">
      <span className={`text-2xl font-bold ${isGood ? 'text-emerald-600' : 'text-red-500'}`}>
        {value}%
      </span>
      {isGood ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
      ) : (
        <AlertTriangle className="h-5 w-5 text-red-500" />
      )}
      <span className="text-xs text-muted-foreground">
        Target: {benchmark}%
      </span>
    </div>
  );
};

export default function HVLDLTrends() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hvldlData, setHvldlData] = useState({ trends: [], summary: {} });
  const [losData, setLosData] = useState({ trends: [] });
  const [months, setMonths] = useState('6');

  useEffect(() => {
    fetchData();
  }, [token, months]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hvldlRes, losRes] = await Promise.all([
        fetch(`${API_URL}/api/metrics/clinical/hvldl-trends?months=${months}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/metrics/clinical/los-trends?months=${months}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (hvldlRes.ok) {
        setHvldlData(await hvldlRes.json());
      }
      if (losRes.ok) {
        setLosData(await losRes.json());
      }
    } catch (error) {
      toast.error('Failed to load trend data');
    } finally {
      setLoading(false);
    }
  };

  const { summary } = hvldlData;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hvldl-trends">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Heart className="h-6 w-6 text-rose-500" />
            HVLDL Trends
          </h2>
          <p className="text-muted-foreground">Hospice Visits in Last Days of Life Quality Metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 Months</SelectItem>
              <SelectItem value="6">6 Months</SelectItem>
              <SelectItem value="12">12 Months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={summary.avg_2_day >= 90 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">RN Visit in Last 2 Days</p>
            <TrendIndicator value={summary.avg_2_day || 0} benchmark={90} label="2-Day" />
            <p className="text-xs text-muted-foreground mt-2">CMS Quality Measure</p>
          </CardContent>
        </Card>

        <Card className={summary.avg_3_day >= 95 ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">RN Visit in Last 3 Days</p>
            <TrendIndicator value={summary.avg_3_day || 0} benchmark={95} label="3-Day" />
            <p className="text-xs text-muted-foreground mt-2">Best Practice Target</p>
          </CardContent>
        </Card>

        <Card className={summary.avg_7_day >= 98 ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Any Visit in Last 7 Days</p>
            <TrendIndicator value={summary.avg_7_day || 0} benchmark={98} label="7-Day" />
            <p className="text-xs text-muted-foreground mt-2">Agency Standard</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="hvldl" className="space-y-4">
        <TabsList>
          <TabsTrigger value="hvldl" className="gap-2">
            <Heart className="h-4 w-4" /> HVLDL Metrics
          </TabsTrigger>
          <TabsTrigger value="los" className="gap-2">
            <Calendar className="h-4 w-4" /> Length of Stay
          </TabsTrigger>
        </TabsList>

        {/* HVLDL Chart */}
        <TabsContent value="hvldl">
          <Card>
            <CardHeader>
              <CardTitle>HVLDL Performance Over Time</CardTitle>
              <CardDescription>
                Tracking RN visits in the last days of life against CMS benchmarks
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hvldlData.trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={hvldlData.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[70, 100]} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <ReferenceLine y={90} stroke="#ef4444" strokeDasharray="5 5" label="CMS Target (90%)" />
                    <Area 
                      type="monotone" 
                      dataKey="hvldl_2_day" 
                      name="2-Day Visit" 
                      stroke="#ef4444" 
                      fill="#fecaca" 
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="hvldl_3_day" 
                      name="3-Day Visit" 
                      stroke="#f59e0b" 
                      fill="#fef3c7" 
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="hvldl_7_day" 
                      name="7-Day Visit" 
                      stroke="#10b981" 
                      fill="#d1fae5" 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Heart className="h-12 w-12 mb-4 opacity-50" />
                  <p>No HVLDL data available yet</p>
                  <p className="text-sm">Enter HVLDL metrics in Data Entry to see trends</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Length of Stay Chart */}
        <TabsContent value="los">
          <Card>
            <CardHeader>
              <CardTitle>Length of Stay and Census Trends</CardTitle>
              <CardDescription>
                Median and mean LOS with ADC tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              {losData.trends.length > 0 ? (
                <div className="space-y-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={losData.trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="median_los" 
                        name="Median LOS (days)" 
                        stroke="#6366f1" 
                        strokeWidth={2}
                        dot={{ fill: '#6366f1' }}
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="mean_los" 
                        name="Mean LOS (days)" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="total_adc" 
                        name="Total ADC" 
                        stroke="#10b981" 
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={losData.trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="live_dc_pct" name="Live Discharge %" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mb-4 opacity-50" />
                  <p>No length of stay data available yet</p>
                  <p className="text-sm">Enter LOS metrics in Data Entry to see trends</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Benchmark Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4 text-[#00b0ab]" />
            HVLDL Quality Benchmarks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200">
              <p className="font-medium text-rose-700">CMS Quality Measure</p>
              <p className="text-rose-600">RN visit in last 2 days of life: 90% or higher</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200">
              <p className="font-medium text-amber-700">Best Practice</p>
              <p className="text-amber-600">RN visit in last 3 days: 95% or higher</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200">
              <p className="font-medium text-emerald-700">Agency Standard</p>
              <p className="text-emerald-600">Any visit in last 7 days: 98% or higher</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
